<template>
  <div class="html-editor">
    <tinymce-editor :init="getTinymceConfig()" :disabled="disabled" :id="id" :rows="rows" :data-cy="dataCy"></tinymce-editor>
  </div>
</template>

<script setup lang="ts">
import getDefaultEditorConfig from "@plugins/tinymce-editor/defaultEditorConfig";
import { smartSnippetPlugin } from "@plugins/tinymce-editor/smartsnippets.js";
import tinymceEditor from "@tinymce/tinymce-vue";
import { Editor, RawEditorOptions } from "tinymce";
import { ref } from "vue";
import { watch } from "vue";
import { computed } from "vue";

const emits = defineEmits(["input", "init", "change", "blur", "focus", "fullscreen-state-changed", "editor-input"]);
const props = defineProps<{
  value?: string;
  id?: string;
  rows?: number | string;
  additionalConfig?: Partial<RawEditorOptions>;
  disabled?: boolean;
  hasSmartSnippets?: boolean;
  articleId?: string;
  dataCy?: string;
}>();

const localValue = computed({
  get: () => {
    return props.value;
  },
  set: (nv) => {
    emits("input", nv);
  }
});

const localEditor = ref<Editor | null>(null);

watch(
  () => props.value,
  (nv) => {
    if (localEditor.value?.getContent() != nv) localEditor.value?.setContent(nv ?? "");
  },
  { immediate: true }
);

const getTinymceConfig = () => {
  const baseConfig = getDefaultEditorConfig();
  baseConfig.article_id = props.articleId;
  baseConfig.setup = (editor) => {
    localEditor.value = editor;
    //@ts-ignore
    if (tinymce.PluginManager) tinymce.PluginManager.add("smartsnippets", smartSnippetPlugin);
    editor.on("init", function (e) {
      editor.setContent(localValue.value ?? "");
      emits("change", e);
    });
    editor.on("input", function (e) {
      localValue.value = editor.getContent();
      emits("editor-input", e);
    });
    editor.on("change", function (e) {
      localValue.value = editor.getContent();
      emits("change", e);
    });
    editor.on("blur", function (e) {
      emits("blur", e);
    });
    editor.on("focus", function (e) {
      emits("focus", e);
    });
    editor.on("FullscreenStateChanged", function (e) {
      emits("fullscreen-state-changed", e);
    });
  };
  return Object.assign(baseConfig, props.additionalConfig);
};

const getEditor = () => {
  return localEditor.value;
};

defineExpose({ getEditor });
</script>
